import { reactive, readonly } from 'vue'
import { AuthPlugin, RequiredAuthOptions } from '../types'
import useProviderData, { savedAuthProvider } from './useProviderData'
import { UserSource } from '@coac-gmbh/saifty-main-apis'
import axios from 'axios'
import { closeSocketConnection } from '@/composables/useSockets'
import { addToast } from '@/composables/useToasts'

export interface OAuth2Configuration {
  clientId: string
  scopes: string
  authorizationEndpoint: string
  tokenEndpoint: string
  userInfoEndpoint: string
  logoutEndpoint: string
  redirectUri: string
}

export const STORAGE_ACCESS_TOKEN_KEY = 'SAIFTY_ACCESS_TOKEN'
const STORAGE_ID_TOKEN = 'SAIFTY_ID_TOKEN'
const STORAGE_REFRESH_TOKEN_KEY = 'SAIFTY_REFRESH_TOKEN'
const STORAGE_PKCE_CODE_VERIFIER = 'SAIFTY_PKCE_CODE_VERIFIER'

export function setupAuthPluginOAuth2(options: RequiredAuthOptions, oauth2Config: OAuth2Configuration): AuthPlugin {
  const router = options.router
  const { isAuthenticated, user, error, loaded, waitUntilLoaded, syncUserWithBackend } = useProviderData()
  initialize()

  // This function is autocalled, this is a workaround to use await without making the entire composable async
  async function initialize() {
    error.value = undefined
    try {
      // If the user is returning to the app after authentication ...
      if (window.location.search.includes('code=')) {
        // handle the redirect and retrieve tokens
        // const { appState } = await handleRedirectCallback()
        await handleRedirectCallback()
        await loadUserState(true)
        // TODO: Redirect to the redirectTo or to the current page without the code|state|error
        // Maybe read the next URL parameter if existing
        // router.push(appState?.redirectTo || window.location.pathname)
        router.push(window.location.pathname)
      } else {
        await loadUserState()
      }
    } catch (exception) {
      console.error('OAuth2 authentication error:', exception)
      error.value = String(exception)
    }
    // When the load of the user state or the authorization code flow fails we assume the user is not authenticated,
    // so that the application gets displayed instead of remaining unloaded
    // public pages would be displayed but private pages would get redirected to the error page
    loaded.value = true
  }

  // Use axios to exchange the authentication code for a token
  async function handleRedirectCallback() {
    if (!window.location.search.includes('code=')) {
      return
    }

    const authorizationCode = new URLSearchParams(window.location.search).get('code')
    const params = new URLSearchParams()
    params.set('grant_type', 'authorization_code')
    params.set('client_id', oauth2Config.clientId)
    params.set('code', authorizationCode?.toString() || '')
    params.set('redirect_uri', oauth2Config.redirectUri)
    params.set('scope', oauth2Config.scopes)
    params.set('code_verifier', localStorage.getItem(STORAGE_PKCE_CODE_VERIFIER) || '')

    const { status, data } = await axios.post(oauth2Config.tokenEndpoint, params)
    if (status === 200) {
      const { access_token } = data
      localStorage.setItem(STORAGE_ACCESS_TOKEN_KEY, access_token)
      if (data.refresh_token) {
        localStorage.setItem(STORAGE_REFRESH_TOKEN_KEY, data.refresh_token)
      }
      if (data.id_token) {
        localStorage.setItem(STORAGE_ID_TOKEN, data.id_token)
      }
    }
  }

  async function refreshAccessToken() {
    const refreshToken = localStorage.getItem(STORAGE_REFRESH_TOKEN_KEY)
    if (!refreshToken) {
      return
    }

    try {
      const params = new URLSearchParams()
      params.set('grant_type', 'refresh_token')
      params.set('client_id', oauth2Config.clientId)
      params.set('refresh_token', refreshToken)
      params.set('scope', oauth2Config.scopes)
      const { status, data } = await axios.post(oauth2Config.tokenEndpoint, params)
      if (status === 200) {
        const { access_token } = data
        localStorage.setItem(STORAGE_ACCESS_TOKEN_KEY, access_token)
        if (data.refresh_token) {
          localStorage.setItem(STORAGE_REFRESH_TOKEN_KEY, data.refresh_token)
        }
        if (data.id_token) {
          localStorage.setItem(STORAGE_ID_TOKEN, data.id_token)
        }
      }
    } catch (e) {
      console.error('Error refreshing the access token:', e)
    }
  }

  function refreshAccessTokenInterval() {
    setInterval(() => {
      refreshAccessToken()
    }, 1000 * 60 * 5) // 5 minutes
  }

  async function loadUserState(fromRedirectCallback = false) {
    const token = await getAccessToken()
    if (!token) {
      isAuthenticated.value = false
      return
    }

    try {
      const { data: oauth2User } = await axios.get(oauth2Config.userInfoEndpoint, {
        headers: { Authorization: `${options.axios?.authorizationHeaderPrefix || 'Bearer'} ${token}` },
      })

      user.value = {
        saiftyId: '',
        ...oauth2User,
        id: oauth2User.sub,
        email: oauth2User.mail || oauth2User.email,
        nickname: oauth2User.first_name,
        just_registered: false,
        permissions: [],
        roles: [],
        isAdmin: (import.meta.env.VITE_ADMIN_EMAILS || window.ENV.ADMIN_EMAILS)
          ?.split(',')
          .map((s) => s.trim())
          .includes(oauth2User?.email || ''),
      }

      isAuthenticated.value = true
      if (isAuthenticated.value && user.value && user.value.email && token) {
        savedAuthProvider.value = 'oauth2'
        const { permissions, roles, saiftyId } = await syncUserWithBackend(
          user.value.email,
          UserSource.Oauth2,
          token
        )
        user.value = {
          ...user.value,
          permissions,
          roles,
          saiftyId,
        }
      }
      refreshAccessTokenInterval()
    } catch (e) {
      localStorage.removeItem(STORAGE_ACCESS_TOKEN_KEY)
      localStorage.removeItem(STORAGE_ID_TOKEN)
      isAuthenticated.value = false
      if (fromRedirectCallback) {
        // When we come from the redirection callback and this endpoint fails, then we throw the error to avoid an infinite redirection loop
        // The error will be caught by the initialize function and the user will be redirected to the error page
        // When the user is not coming from the redirection callback, then we assume the user is not authenticated
        throw e
      }
    }
  }

  function authenticationError() {
    const urlError = new URLSearchParams()
    urlError.set('statusCode', '500')
    urlError.set('message', 'There was an error while authenticating')
    window.location.href = `/error?${urlError.toString()}`
  }

  // PKCE Functions
  async function generateCodeChallenge(codeVerifier: string) {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier))

    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
  }

  function generateRandomString(length: number) {
    let text = ''
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'

    for (let i = 0; i < length; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length))
    }

    return text
  }
  // End PKCE Functions

  async function loginWithRedirect(redirectTo?: string) {
    const codeVerifier = generateRandomString(64) // PKCE
    const codeChallenge = await generateCodeChallenge(codeVerifier) // PKCE
    localStorage.setItem(STORAGE_PKCE_CODE_VERIFIER, codeVerifier)

    let authorizationURL = `${oauth2Config.authorizationEndpoint}?response_type=code&client_id=${
      oauth2Config.clientId
    }&scope=${encodeURIComponent(oauth2Config.scopes)}&redirect_uri=${
      oauth2Config.redirectUri
    }&code_challenge_method=S256&code_challenge=${codeChallenge}`

    if (redirectTo) {
      authorizationURL += `&next=${redirectTo}`
    }
    window.location.href = authorizationURL
  }

  async function logout() {
    const params = new URLSearchParams()
    params.set('client_id', oauth2Config.clientId)
    params.set('token', await getAccessToken())

    try {
      await axios.post(oauth2Config.logoutEndpoint, params)
    } finally {
      // Even if the token revoke fails we assume the user is logged out
      localStorage.removeItem(STORAGE_ACCESS_TOKEN_KEY)
      localStorage.removeItem(STORAGE_ID_TOKEN)
      closeSocketConnection()
      isAuthenticated.value = false
      user.value = undefined
      savedAuthProvider.value = null
    }
  }

  async function signup() {
    loginWithRedirect()
  }

  async function getAccessToken() {
    return localStorage.getItem(STORAGE_ACCESS_TOKEN_KEY) || ''
  }

  async function getIdToken() {
    return localStorage.getItem(STORAGE_ID_TOKEN) || ''
  }

  async function changePassword() {
    addToast({ text: 'Not implemented', variant: 'warning' })
  }

  const unWrappedRefs = reactive({
    isAuthenticated,
    user,
    loaded,
    error,
    loginWithRedirect,
    signup,
    logout,
    getAccessToken,
    getIdToken,
    waitUntilLoaded,
    changePassword,
  })

  return readonly(unWrappedRefs)
}
